/**
 * `/cuenta` — eliminar la cuenta (§16.2).
 *
 * **La palabra escrita a mano no es fricción decorativa.** Es irreversible, y la diferencia entre
 * un clic distraído y escribir siete letras es la diferencia entre perder una cuenta sin querer y
 * decidirlo. El botón está deshabilitado hasta que la palabra coincide.
 *
 * Y se explica **qué se conserva**: la anonimización no borra las respuestas, y decirlo evita que
 * alguien elimine su cuenta creyendo que borra el rastro de una partida que no le gustó.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { SessionProfile } from '@clica/shared';
import { ApiRequestError, deleteAccount } from '../api/client.js';
import { useSession } from '../auth/session.js';
import { Boton } from '../components/sistema/boton.js';
import { Marco } from '../components/sistema/marco.js';

type Estado = 'idle' | 'deleting' | 'done' | 'failed' | 'lastAdmin';

export function PantallaCuenta({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const signOut = useSession((store) => store.signOut);
  const [texto, setTexto] = useState('');
  const [estado, setEstado] = useState<Estado>('idle');

  const palabra = t('account.confirmWord');
  const coincide = texto.trim() === palabra;

  if (estado === 'done') {
    /*
     * **La sesión se cierra al pulsar, no automáticamente.**
     *
     * Cerrarla en el acto redirige a la pantalla de acceso y la confirmación no llega a leerse:
     * quien acaba de hacer algo irreversible se queda sin saber si funcionó. La cuenta ya no
     * existe, así que el token no sirve para nada aunque siga en memoria unos segundos más.
     */
    return (
      <Marco profile={profile} migas={[{ texto: t('account.title') }]}>
        <main id="contenido" className="mx-auto max-w-2xl p-6">
          <p role="status" className="text-lead font-display font-bold">
            {t('account.done')}
          </p>
          {/* Con filete de tinta: el relleno hi-vis da 1,16:1 contra el papel y sin borde no
              tiene límite de componente ninguno. */}
          <Boton variante="primaria" className="mt-6" onClick={() => void signOut()}>
            {t('auth.signOut')}
          </Boton>
        </main>
      </Marco>
    );
  }

  return (
    <Marco profile={profile} migas={[{ texto: t('account.title') }]}>
      <main id="contenido" className="mx-auto max-w-2xl p-6">
        <h1 className="font-display text-h1 font-extrabold text-balance">{t('account.title')}</h1>

        <section
          aria-labelledby="eliminar-titulo"
          className="border-ko/60 mt-8 rounded-xl border p-5"
        >
          <h2 id="eliminar-titulo" className="text-ko text-xl font-semibold">
            {t('account.deleteTitle')}
          </h2>
          <p className="mt-3">{t('account.deleteLead')}</p>
          <p className="mt-2 font-semibold">{t('account.deleteIrreversible')}</p>

          <form
            className="mt-6 flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!coincide) return;
              setEstado('deleting');
              deleteAccount()
                .then(() => {
                  setEstado('done');
                })
                .catch((cause: unknown) => {
                  setEstado(
                    cause instanceof ApiRequestError && cause.key === 'account.error.last_admin'
                      ? 'lastAdmin'
                      : 'failed',
                  );
                });
            }}
          >
            <label htmlFor="confirmacion" className="font-semibold">
              {t('account.confirmLabel')}
            </label>
            <input
              id="confirmacion"
              value={texto}
              autoComplete="off"
              onChange={(event) => setTexto(event.target.value)}
              aria-describedby="confirmacion-ayuda"
              className="rounded-chip border-line bg-surface text-body border-2 px-3 py-2.5 font-mono"
            />
            <p id="confirmacion-ayuda" className="text-micro text-text-dim">
              {t('account.confirmMismatch')}
            </p>

            <button
              type="submit"
              disabled={!coincide || estado === 'deleting'}
              className="border-ko text-ko self-start rounded-lg border-2 px-4 py-2 font-semibold disabled:opacity-50"
            >
              {estado === 'deleting' ? t('account.deleting') : t('account.delete')}
            </button>

            {estado === 'lastAdmin' ? (
              <p role="alert" className="text-ko">
                <span aria-hidden="true">⚠ </span>
                {t('account.lastAdmin')}
              </p>
            ) : null}
            {estado === 'failed' ? (
              <p role="alert" className="text-ko">
                <span aria-hidden="true">⚠ </span>
                {t('account.failed')}
              </p>
            ) : null}
          </form>
        </section>

        <p className="mt-8">
          <Link
            to="/privacidad"
            className="rounded-chip inline-flex min-h-11 items-center underline underline-offset-4"
          >
            {t('privacy.link')}
          </Link>
        </p>
      </main>
    </Marco>
  );
}
